import React from "react";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";

export interface DeleteConfirmationModalProps {
    open: boolean;
    step: number;
    schedule: {
        employee?: { name?: string };
        shift?: { shift_name?: string; start_time?: string; end_time?: string };
    } | null;
    onCancel: () => void;
    onFirstConfirm: () => void;
    onConfirm: () => void;
    isDeleting: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
    open,
    step,
    schedule,
    onCancel,
    onFirstConfirm,
    onConfirm,
    isDeleting,
}) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1600]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md m-4 animate-in fade-in duration-200">
                {step === 1 ? (
                    <>
                        <div className="p-6">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full">
                                <AlertTriangle className="w-8 h-8 text-orange-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Delete Schedule?</h3>
                            <p className="text-gray-600 text-center mb-2">
                                Are you sure you want to delete this schedule?
                            </p>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                <div className="text-sm text-blue-900">
                                    <div className="font-semibold mb-1">Schedule Details:</div>
                                    <div className="text-xs space-y-1">
                                        <div>
                                            <span className="font-medium">Employee:</span>{" "}
                                            {schedule?.employee?.name}
                                        </div>
                                        <div>
                                            <span className="font-medium">Shift:</span>{" "}
                                            {schedule?.shift?.shift_name}
                                        </div>
                                        <div>
                                            <span className="font-medium">Time:</span>{" "}
                                            {schedule?.shift?.start_time} - {schedule?.shift?.end_time}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-red-600 text-center font-medium">
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="flex gap-3 p-6 bg-gray-50 rounded-b-xl border-t border-gray-200">
                            <button
                                onClick={onCancel}
                                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onFirstConfirm}
                                className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                            >
                                Continue
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="p-6">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                                <AlertTriangle className="w-8 h-8 text-red-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Final Confirmation</h3>
                            <p className="text-gray-600 text-center mb-4">This is your last chance to cancel.</p>
                            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4">
                                <p className="text-sm text-red-900 text-center font-semibold">
                                    ⚠️ Are you absolutely sure you want to permanently delete this schedule?
                                </p>
                            </div>
                            <p className="text-xs text-gray-500 text-center">
                                Once deleted, this schedule cannot be recovered.
                            </p>
                        </div>
                        <div className="flex gap-3 p-6 bg-gray-50 rounded-b-xl border-t border-gray-200">
                            <button
                                onClick={onCancel}
                                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                            >
                                No, Keep It
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={isDeleting}
                                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="animate-spin" size={16} />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Yes, Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DeleteConfirmationModal;
